import { writable } from 'svelte/store';
import { getHybrisResponseAsync } from '../../modules/common/services/hybris';
import { getInStockProductsById } from '../../modules/common/services/unbxd';
import { APP_CONSTANTS } from '../config/app-constants';
import { getCookie, isMobileWidth } from '../util';

type Banner = {
  index?: number;
  creativeFormat?: string;
  redirectionUrl?: string;
  creativeAsset?: {
    bannerImageUrl?: string;
    format?: string;
  };
};

function createMiraklSponsoredProducts() {
  const { subscribe, set } = writable([]);

  return {
    subscribe,
    setProducts: (sponsoredProductList: any[][]) => {
      set(sponsoredProductList);
    },
  };
}

function createDisplayBanners() {
  const { subscribe, set } = writable([]);

  return {
    subscribe,
    setBanners: (banners: any[]) => set(banners || []),
  };
}

export const displayBanners = createDisplayBanners();

export const miraklSponsoredProducts = createMiraklSponsoredProducts();

/**
 * This function gets products data using the mirakl sponsored item list.
 * It also merged mirakl related data in the unbxd response.
 */
export async function populateMiraklSponsoredProducts(adserverResponse: any) {
  if ('miraklads' in window.ACC.config && window.ACC.config.miraklads.enabled) {
    if (adserverResponse?.displayBanners?.length > 0) {
      displayBanners.setBanners(adserverResponse.displayBanners);
    } else {
      displayBanners.setBanners([]);
    }
    // mirakl is enabled, proceed with the population
    const hasSponsoredProducts =
      adserverResponse?.extraMiraklAdsSponsoredProduct?.length > 0 &&
      adserverResponse.extraMiraklAdsSponsoredProduct?.some(adUnit => adUnit?.products?.length > 0);
    if (hasSponsoredProducts) {
      const allSponsoredProductCodes = adserverResponse.extraMiraklAdsSponsoredProduct
        .map((pa: any) => pa.products)
        .flat()
        .map((x: any) => x.productId);
      const unxResponseForMirakl = await getInStockProductsById(allSponsoredProductCodes); // getProductById(allSponsoredProductCodes);
      // populate mirakl ads related data in the product data from unbxd
      const miraklSponsoredProductsData: any[][] = [];
      for (const productAd of adserverResponse.extraMiraklAdsSponsoredProduct) {
        const adUnitProducts = [];
        for (let p = 0; p < productAd.products.length; p++) {
          const product = productAd.products[p];
          const productId = product.productId;
          const targetUnbxdProductIndex = unxResponseForMirakl.response.products.findIndex(
            (p: any) => p.productCode === productId
          );
          if (targetUnbxdProductIndex > -1) {
            // product is availabe from unbxd
            unxResponseForMirakl.response.products[targetUnbxdProductIndex].miraklad = {
              index: p + productAd.index,
              adUnitId: productAd.adUnitId,
              adUnitSize: productAd.adUnitSize,
              ...product,
            };

            adUnitProducts.push(unxResponseForMirakl.response.products[targetUnbxdProductIndex]);
          }
        }
        miraklSponsoredProductsData.push(adUnitProducts);
      }
      miraklSponsoredProducts.setProducts(miraklSponsoredProductsData);
    } else {
      console.warn('[dbg] miraklads sponsored products config NOT present');
      miraklSponsoredProducts.setProducts([]);
    }
  } else {
    console.error('[dbg] Cannot find ACC.config.miraklads or ACC.config.miraklads.enabled = false');
  }
}

export type HybrisMiraklRequest = {
  productId?: string;
  categoryId?: string;
  searchKeyword?: string;
  pageType: 'PDP' | 'CATEGORY' | 'SEARCH';
  filters?: any;
  device?: 'MOBILE' | 'DESKTOP';
};

/**
 * Avoid calling this function directly. if possible. This function is used internally by other methods.
 * Get Mirakl sponsored products from Hybris.
 * @param requestOpts {HybrisMiraklRequest} request object
 * @returns {Promise} - A promise resolving to the response of the request.
 */
export async function getMiraklSponsoredProductsAsync(requestOpts: HybrisMiraklRequest): Promise<any> {
  if ('filters' in requestOpts && requestOpts.filters.selected.length > 0) {
    requestOpts.filters = requestOpts.filters.selected.map(transformFilterToMiraklRequestParam).join(':');
  } else {
    delete requestOpts.filters;
  }

  const fixedOpts = {
    fields: 'FULL',
    userId: getCookie('mirakl-analytics') || '',
  };
  const mergedOpts = { ...fixedOpts, ...requestOpts };
  const adserverResponse = await getHybrisResponseAsync(APP_CONSTANTS.GET_MIRAKL_SPONSORED_PRODUCTS, mergedOpts);
  return populateMiraklSponsoredProducts(adserverResponse);
}

/**
 * For PDP only. Get Mirakl sponsored products for inserting into product carousel.
 * @param productId - Product id of the PDP.
 */
export function getMiraklSponsoredProductsPDPAsync(productId: string) {
  return getMiraklSponsoredProductsAsync({
    productId,
    pageType: 'PDP',
    device: isMobileWidth() ? 'MOBILE' : 'DESKTOP',
  });
}

/**
 * For PLP only. Get Mirakl sponsored products for inserting into product list.
 * @param categoryId - Category ID of the results page.
 * @param filters - Output of unxFacets (For selected filters)
 */
export function getMiraklSponsoredProductsPLPAsync(categoryId: string, filters: any) {
  return getMiraklSponsoredProductsAsync({
    categoryId,
    filters,
    pageType: 'CATEGORY',
    device: isMobileWidth() ? 'MOBILE' : 'DESKTOP',
  });
}

/**
 * For Search page. Get Mirakl sponsored products for inserting into product carousel.
 * @param searchKeyword - Search term used
 * @param filters - Output of unxFacets (For selected filters)
 */
export function getMiraklSponsoredProductsSearchAsync(searchKeyword: string, filters: any) {
  return getMiraklSponsoredProductsAsync({
    searchKeyword,
    filters,
    pageType: 'SEARCH',
    device: isMobileWidth() ? 'MOBILE' : 'DESKTOP',
  });
}

/**
 * Converts a single filter facet from unxFacets to a URLSearchParam string
 * @param f - single item from unxFacets.
 */
export function transformFilterToMiraklRequestParam(f: any) {
  if (f.facetName === 'price' || f.facetName === 'rating') {
    return `${f.facetName?.replace('_uFilter', '')}:${transformRangeToMiraklRequestParamValue(f.values[0])}`;
  }
  return `${f.facetName?.replace('_uFilter', '')}:${f.values.join(',')}`;
}

function transformRangeToMiraklRequestParamValue(range: string) {
  if (range?.length > 0 && range[0] === '[') {
    return range.substring(1, range.length - 1).replace(' TO ', ',');
  }
  return range;
}

export function createBannerMap(banners: Banner[]) {
  const map: Record<number, Banner> = {};

  if (!banners || banners.length === 0) return map;

  banners.forEach((banner, i) => {
    const index = banner.index ?? (i + 1) * 0;

    map[index] = banner;
  });
  return map;
}

type Product = {
  id: string | number;
  offerId?: string | number;
  [key: string]: any;
};

/**
 * Reorders products by moving sponsored products (products with `offerId`)
 * to the configured positions using a swap operation.
 *
 * Example:
 * products = [S1, N1, N2, N3, S2, N4]
 * config   = "3,5"
 *
 * Result:
 * [N2, N1, S1, N3, S2, N4]
 *
 * Notes:
 * - Config positions are 1-based (e.g. "3,5" means indexes 2 and 4).
 * - Only swaps are performed; no products are removed or duplicated.
 * - Sponsored products are processed in the order they appear in the array.
 * - If there are more configured positions than sponsored products,
 *   the remaining positions are ignored.
 *
 * @param products - List of products to reorder.
 * @param config - Comma-separated list of target positions for sponsored products.
 * @returns A new array with sponsored products moved to the configured positions.
 */
export function reorderProductsWithConfig(products: Product[], config: string): Product[] {
  if (!products?.length || !config) return products;

  const result = [...products];

  // Convert config from 1-based positions to 0-based indexes.
  // Example: "3,5" -> [2, 4]
  const targetPositions = config
    .split(',')
    .map(pos => parseInt(pos.trim(), 10) - 1)
    .filter(pos => !isNaN(pos) && pos >= 0 && pos < result.length);

  // Tracks where to start searching for the next sponsored product
  // to avoid re-processing previously moved items.
  let searchStart = 0;

  for (const targetPos of targetPositions) {
    // Find the next sponsored product (offerId present)
    const offerIndex = result.findIndex((product, index) => index >= searchStart && product.offerId);

    // No more sponsored products available
    if (offerIndex === -1) {
      break;
    }

    // Move the sponsored product to the configured position
    // by swapping it with the item currently at that position.
    if (offerIndex !== targetPos) {
      [result[offerIndex], result[targetPos]] = [result[targetPos], result[offerIndex]];
    }

    // Continue searching after the current target position
    // for the next sponsored product.
    searchStart = targetPos + 1;
  }

  return result;
}
